/*
  Resource Library — client.

  The app proxy (window.LibraryDataSource) is the single source of truth: this
  fetches the current page on first paint and on every filter / search / sort /
  page change, and renders the returned cards, facet counts, chips, count and
  pagination. The URL is kept in sync so filtered views are shareable. Liquid
  ships only the chrome (toolbar, facet controls, skeletons).

  Also handles the card<->list view toggle (persisted), the mobile filter
  drawer, saving to the customer metafield, and emailing a document link.
*/
(function () {
  'use strict';

  var OWNED = ['category', 'product_line', 'q', 'sort', 'page', 'saved'];
  var GROUPS = ['category', 'product_line'];
  var SEARCH_DEBOUNCE = 300;
  var PENDING_SAVE_KEY = 'library:pendingSave';
  var VIEW_KEY = 'library:view';
  var SIZE_CACHE_KEY = 'library:sizes';

  function slice(nodes) { return Array.prototype.slice.call(nodes); }

  // localStorage guarded for private mode / disabled storage.
  function storageGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function storageSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* ignore */ }
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function format(template, values) {
    if (!template) return '';
    return Object.keys(values || {}).reduce(function (out, key) {
      return out.split('__' + key + '__').join(values[key]);
    }, template);
  }

  function el(tag, className) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    return node;
  }

  /* ------------------------------------------------------------------ *
   * File size. The proxy doesn't carry byte counts, so we ask the file
   * host with a HEAD request and read Content-Length — cheap (no body)
   * and CORS-safelisted, so it works against the Shopify CDN. Answers are
   * memoised per URL for the session; a host that blocks CORS or hides the
   * length resolves to 0 and the card simply shows no size.
   * ------------------------------------------------------------------ */
  var sizeCache = readSizeCache();
  var sizePending = {};

  function readSizeCache() {
    try { return JSON.parse(window.sessionStorage.getItem(SIZE_CACHE_KEY)) || {}; }
    catch (e) { return {}; }
  }

  // Only successful lookups are persisted — a 0 (CORS refused, no
  // Content-Length, offline) stays in memory so the next page load retries.
  var persistSizeCache = debounce(function () {
    var known = {};
    Object.keys(sizeCache).forEach(function (url) { if (sizeCache[url] > 0) known[url] = sizeCache[url]; });
    try { window.sessionStorage.setItem(SIZE_CACHE_KEY, JSON.stringify(known)); }
    catch (e) { /* private mode / quota */ }
  }, 500);

  function fetchSize(url) {
    if (Object.prototype.hasOwnProperty.call(sizeCache, url)) return Promise.resolve(sizeCache[url]);
    if (sizePending[url]) return sizePending[url];
    // No custom headers: a HEAD with only safelisted headers stays a "simple"
    // request, so the CDN answers it without a CORS preflight.
    var p = fetch(url, { method: 'HEAD', credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) return 0;
        var len = parseInt(res.headers.get('content-length'), 10);
        return len > 0 ? len : 0;
      })
      .catch(function () { return 0; })   // CORS, offline, 4xx — no size shown
      .then(function (bytes) {
        sizeCache[url] = bytes;
        delete sizePending[url];
        persistSizeCache();
        return bytes;
      });
    sizePending[url] = p;
    return p;
  }

  // Bytes -> "820 KB" / "2.4 MB", using the translated unit templates.
  function formatSize(bytes, strings) {
    if (!(bytes > 0)) return '';
    var kb = bytes / 1024;
    if (kb < 1024) return format(strings.size_kb, { SIZE: decimal(Math.max(1, Math.round(kb)), 0) });
    var mb = kb / 1024;
    return format(strings.size_mb, { SIZE: decimal(mb, mb < 10 ? 1 : 0) });
  }

  function decimal(n, digits) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
      }).format(n);
    } catch (e) { return n.toFixed(digits); }
  }

  // Trusted sources. A HEAD only returns a length when the host allows the
  // cross-origin read, so the merchant lists the hosts worth asking. One entry
  // per line (or comma-separated); a bare domain covers its subdomains. The
  // shop's own host is always allowed — same-origin needs no CORS.
  function parseSources(raw) {
    return String(raw || '')
      .split(/[\s,]+/)
      .map(function (entry) {
        return entry.trim().toLowerCase()
          .replace(/^https?:\/\//, '')   // full URLs are fine to paste
          .replace(/\/.*$/, '')          // drop any path
          .replace(/^www\./, '');
      })
      .filter(Boolean);
  }

  function isTrustedSource(url, sources) {
    var host;
    try { host = new URL(url, window.location.href).host.toLowerCase(); }
    catch (e) { return false; }
    if (host === window.location.host.toLowerCase()) return true;
    host = host.replace(/^www\./, '');
    return sources.some(function (source) {
      return host === source || host.slice(-(source.length + 1)) === '.' + source;
    });
  }

  // Hosts whose links are always a video, subdomains included.
  var VIDEO_HOSTS = [
    'youtube.com', 'youtu.be', 'youtube-nocookie.com', 'vimeo.com', 'wistia.com', 'wistia.net',
    'loom.com', 'vidyard.com', 'dailymotion.com', 'dai.ly', 'brightcove.net', 'jwplayer.com',
    'streamable.com', 'twitch.tv', 'tiktok.com', 'rumble.com'
  ];
  var VIDEO_FORMAT = /^(?:video\/.+|mp4|m4v|mov|qt|webm|ogv|avi|wmv|mkv|flv|mpe?g|3gp|m3u8)$/i;

  function isVideoHost(host) {
    host = host.toLowerCase().replace(/^www\./, '');
    return VIDEO_HOSTS.some(function (h) {
      return host === h || host.slice(-(h.length + 1)) === '.' + h;
    });
  }

  // The proxy's file_format when it has one, else the URL's extension. Video
  // platform links and video files collapse to a single VIDEO label.
  function fileFormat(resource) {
    var url = resource.file_url || '';
    var host = '';
    var path = url;
    try {
      var parsed = new URL(url, window.location.href);
      host = parsed.host;
      path = parsed.pathname;
    } catch (e) { path = path.split('?')[0]; }
    if (url && isVideoHost(host)) return 'VIDEO';

    var match = /\.([a-z0-9]{2,5})$/i.exec(path);
    var fmt = resource.file_format ? String(resource.file_format) : (match ? match[1] : '');
    if (VIDEO_FORMAT.test(fmt) || (match && VIDEO_FORMAT.test(match[1]))) return 'VIDEO';
    return fmt.toUpperCase();
  }

  // Only ask for sizes the visitor is about to see — a page of cards would
  // otherwise fire two dozen requests on first paint. The observed node is the
  // card, never the size span: that span starts hidden (display:none), and a
  // node with no box never intersects, so observing it would never fire.
  var sizeObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          hydrateSizes(entry.target);
        });
      }, { rootMargin: '200px' })
    : null;

  function observeSizes(card) {
    card.setAttribute('data-size-scope', '');
    if (sizeObserver) sizeObserver.observe(card);
    else hydrateSizes(card);
  }

  // Drop observers for cards that are about to be replaced.
  function releaseSizeNodes(container) {
    if (!sizeObserver) return;
    slice(container.querySelectorAll('[data-size-scope]')).forEach(function (n) { sizeObserver.unobserve(n); });
  }

  function hydrateSizes(card) {
    slice(card.querySelectorAll('[data-file-url]')).forEach(hydrateSize);
  }

  function hydrateSize(node) {
    var url = node.getAttribute('data-file-url');
    if (!url) return;
    var strings = node.__strings || {};
    fetchSize(url).then(function (bytes) {
      // The node may have been swapped out by a newer render mid-flight.
      if (!node.isConnected) return;
      var text = formatSize(bytes, strings);
      if (!text) return;
      node.textContent = text;
      node.hidden = false;
    });
  }

  // Navigate via a transient anchor (mailto opens the mail client without
  // unloading; a normal URL follows the link).
  function navigate(url) {
    var a = document.createElement('a');
    a.href = url;
    (document.body || document.documentElement).appendChild(a);
    a.click();
    a.remove();
  }

  // ---- icons (match the theme's icon snippets) ------------------------
  function svg(inner, cls, viewBox) {
    return '<svg class="icon ' + cls + '" aria-hidden="true" focusable="false" viewBox="' + viewBox + '">' + inner + '</svg>';
  }
  var ICON = {
    download: svg('<path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32" d="M256 64v264"/><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32" d="M152 224l104 104 104-104"/><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32" d="M64 352v40a56 56 0 0056 56h272a56 56 0 0056-56v-40"/>', 'icon-download', '0 0 512 512'),
    email: svg('<rect x="48" y="96" width="416" height="320" rx="40" ry="40" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32"/><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32" d="M112 160l144 112 144-112"/>', 'icon-email', '0 0 512 512'),
    bookmark: svg('<path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32" d="M352 48H160a48 48 0 00-48 48v368l144-128 144 128V96a48 48 0 00-48-48z"/>', 'icon-bookmark', '0 0 512 512'),
    caret: svg('<path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="48" d="M112 184l144 144 144-144"/>', 'icon-caret', '0 0 512 512'),
    closeSmall: '<svg class="icon icon-close-small" aria-hidden="true" focusable="false" viewBox="0 0 12 13" fill="none"><path d="M8.49 9.33 2.83 3.67M2.89 9.39l5.54-5.77" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  };

  /* ------------------------------------------------------------------ *
   * View toggle (card <-> list), class-only.
   * ------------------------------------------------------------------ */
  function initViewToggle(root) {
    var buttons = slice(root.querySelectorAll('[data-library-view]'));
    if (!buttons.length) return;
    var grid = root.querySelector('[data-library-results]');
    var gridClasses = grid ? slice(grid.classList).filter(function (n) { return /^grid(--|$)/.test(n); }) : [];

    function apply(view) {
      var isList = view === 'list';
      root.classList.toggle('library--list', isList);
      root.classList.toggle('library--card', !isList);
      buttons.forEach(function (b) {
        var active = b.getAttribute('data-library-view') === view;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      if (grid) {
        if (isList) { gridClasses.forEach(function (n) { grid.classList.remove(n); }); grid.classList.add('library-list'); }
        else { grid.classList.remove('library-list'); gridClasses.forEach(function (n) { grid.classList.add(n); }); }
      }
    }
    root.__setView = apply;
    // Restore the visitor's last choice; fall back to the SSR default.
    var stored = storageGet(VIEW_KEY);
    apply(stored === 'list' || stored === 'card'
      ? stored
      : (root.classList.contains('library--list') ? 'list' : 'card'));
    buttons.forEach(function (b) {
      b.addEventListener('click', function () {
        var view = b.getAttribute('data-library-view');
        apply(view);
        storageSet(VIEW_KEY, view);
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Mobile filter drawer.
   * ------------------------------------------------------------------ */
  function initFilterDrawer(root) {
    var opener = root.querySelector('[data-library-filter-open]');
    if (!opener) return;
    var closers = slice(root.querySelectorAll('[data-library-filter-close]'));
    var closeButton = root.querySelector('.library__sidebar-close');
    function setOpen(open) {
      root.classList.toggle('library--filters-open', open);
      opener.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.classList.toggle('overflow-hidden', open);
      if (open && closeButton) closeButton.focus();
      else if (!open) opener.focus();
    }
    opener.addEventListener('click', function () { setOpen(true); });
    closers.forEach(function (c) { c.addEventListener('click', function () { setOpen(false); }); });
    root.addEventListener('keyup', function (e) {
      if (e.key === 'Escape' && root.classList.contains('library--filters-open')) setOpen(false);
    });
  }

  /* ------------------------------------------------------------------ *
   * Filtering via the proxy.
   * ------------------------------------------------------------------ */
  function initLibrary(root) {
    var resultsEl = root.querySelector('[data-library-results]');
    if (!resultsEl) return;

    var perPage = parseInt(root.dataset.perPage, 10) || 24;
    var defaultSort = root.dataset.defaultSort || 'newest';
    var ratioClass = root.dataset.ratioClass || 'library-card--ratio-portrait';
    var cardScheme = root.dataset.cardScheme || 'scheme-1';
    var cardShadow = root.dataset.cardShadow === '1';
    // No-thumbnail tiles use the section's colour scheme.
    var tileScheme = root.dataset.sectionScheme || cardScheme;
    var showSize = root.dataset.showFileSize === '1';
    var sizeSources = parseSources(root.dataset.fileSizeSources);
    var strings = readStrings(root);

    // Saved resources (customer metafield) — reads are SSR; only toggles fetch.
    var loggedIn = root.dataset.loggedIn === '1';
    var loginBase = root.dataset.loginUrl || '/account/login';
    var savedSet = new Set((root.dataset.librarySaved || '').split(/\s+/).filter(Boolean));

    // Build the login URL fresh at click time so the return URL carries the
    // visitor's current path AND query params (their filters survive login).
    function loginUrlNow() {
      var ret = window.location.pathname + window.location.search;
      return loginBase + (loginBase.indexOf('?') > -1 ? '&' : '?') + 'return_url=' + encodeURIComponent(ret);
    }

    // Polite live region for save feedback (errors etc.).
    var live = el('p', 'visually-hidden');
    live.setAttribute('aria-live', 'polite');
    root.appendChild(live);
    function announce(msg) { live.textContent = ''; live.textContent = msg || ''; }

    var countEl = root.querySelector('[data-library-count]');
    var chipsEl = root.querySelector('[data-library-chips]');
    var stateEl = root.querySelector('[data-library-state]');
    var pagerEl = root.querySelector('[data-library-pagination]');
    var searchForm = root.querySelector('.library__search');
    var searchInput = searchForm ? searchForm.querySelector('input[name="q"]') : null;
    var sortSelects = slice(root.querySelectorAll('[data-library-sort]'));
    var facetInputs = slice(root.querySelectorAll('input[data-library-facet]'));

    var valueMeta = {};
    var groupLabels = {};
    facetInputs.forEach(function (input) {
      var key = input.getAttribute('data-library-facet');
      var li = input.closest('.facets__item') || input.parentElement;
      var lbl = li.querySelector('.facet-checkbox__text-label');
      var groupEl = input.closest('.library__facet-group');
      var groupLabel = groupEl ? groupEl.querySelector('.facets__summary-label') : null;
      valueMeta[key + ':' + input.value] = {
        label: lbl ? lbl.textContent.trim() : input.value,
        groupLabel: groupLabel ? groupLabel.textContent.trim() : key
      };
      if (groupLabel) groupLabels[key] = groupLabel.textContent.trim();
    });

    var state = parseState();
    var controller = null;

    function parseState() {
      var p = new URLSearchParams(window.location.search);
      var s = {
        q: (p.get('q') || '').trim(),
        sort: p.get('sort') || defaultSort,
        page: Math.max(1, parseInt(p.get('page'), 10) || 1),
        saved: p.get('saved') === '1' || p.get('saved') === 'true'
      };
      GROUPS.forEach(function (g) { var r = p.get(g); s[g] = r ? r.split(',').filter(Boolean) : []; });
      return s;
    }

    function buildUrl() {
      var p = new URLSearchParams(window.location.search);
      OWNED.forEach(function (k) { p.delete(k); });
      if (state.q) p.set('q', state.q);
      GROUPS.forEach(function (g) { if (state[g].length) p.set(g, state[g].join(',')); });
      if (state.saved) p.set('saved', '1');
      if (state.sort !== defaultSort) p.set('sort', state.sort);
      if (state.page > 1) p.set('page', String(state.page));
      var qs = p.toString();
      return window.location.pathname + (qs ? '?' + qs : '');
    }

    function pushUrl() { window.history.pushState({ library: true }, '', buildUrl()); }

    // ---- data ---------------------------------------------------------
    // The proxy is the single source of truth — every load fetches, including
    // first paint. Liquid only ships skeletons + the facet controls.
    function load(opts) {
      opts = opts || {};
      if (controller) controller.abort();
      controller = new AbortController();
      var mine = controller;
      renderLoading();

      window.LibraryDataSource.search(
        { q: state.q, category: state.category, product_line: state.product_line, saved: state.saved, sort: state.sort, page: state.page, per_page: perPage },
        { signal: mine.signal }
      ).then(function (resp) {
        if (controller !== mine) return; // superseded by a newer request
        renderResults(resp);
        if (opts.scroll) scrollToTop();
      }).catch(function (err) {
        if (controller !== mine || (err && err.name === 'AbortError')) return;
        renderError();
      });
    }

    // ---- rendering ----------------------------------------------------
    function renderResults(resp) {
      resultsEl.setAttribute('aria-busy', 'false');
      var results = resp.results || [];
      releaseSizeNodes(resultsEl);
      var frag = document.createDocumentFragment();
      results.forEach(function (r) { frag.appendChild(buildCard(r)); });
      resultsEl.replaceChildren(frag);
      resultsEl.hidden = results.length === 0;

      renderCount(resp.pagination.total_results);
      renderFacets(resp.facets || {});
      renderChips();
      renderPager(resp.pagination.total_pages, resp.pagination.page);
      renderEmpty(results.length, resp.meta && resp.meta.query);
      state.page = resp.pagination.page;
    }

    function buildCard(r) {
      var li = el('li', 'grid__item library-item');

      var wrapper = el('div', 'library-card-wrapper card-wrapper');
      var card = el('div', 'card library-card ' + ratioClass + ' contains-card--product card--card card--media' +
        (cardShadow ? ' card--shadow' : '') + ' color-' + cardScheme + ' gradient');
      wrapper.appendChild(card);
      li.appendChild(wrapper);

      var cardUrl = r.file_url || '';
      var isExternal = cardUrl && !/\/cdn\.shopify\.com\//.test(cardUrl) && !sameHost(cardUrl);
      var fileFmt = fileFormat(r);

      if (cardUrl) {
        var full = el('a', 'library-card__full-link');
        full.href = cardUrl;
        full.setAttribute('aria-hidden', 'true');
        full.tabIndex = -1;
        card.appendChild(full);
      }

      var band = el('div', 'card__inner ratio library-card__band');
      var media = el('div', 'card__media');
      band.appendChild(media);
      card.appendChild(band);

      if (r.thumbnail && r.thumbnail.src) {
        var m = el('div', 'media media--transparent library-card__media');
        var img = el('img', 'library-card__image motion-reduce object-top');
        img.src = addWidth(r.thumbnail.src, 800);
        img.srcset = [400, 800, 1200].map(function (w) { return addWidth(r.thumbnail.src, w) + ' ' + w + 'w'; }).join(', ');
        img.sizes = '(min-width: 990px) 300px, (min-width: 750px) 33vw, 100vw';
        img.alt = r.thumbnail.alt || r.title || '';
        img.width = 800; img.height = 1035;
        img.loading = 'lazy'; img.decoding = 'async'; img.setAttribute('fetchpriority', 'low');
        m.appendChild(img);
        media.appendChild(m);
      } else {
        var tile = el('div', 'media media--transparent library-card__tile gradient color-' + tileScheme);
        var fmt = el('span', 'library-card__tile-format');
        fmt.setAttribute('aria-hidden', 'true');
        fmt.textContent = fileFmt || 'FILE';
        tile.appendChild(fmt);
        media.appendChild(tile);
      }

      var body = el('div', 'library-card__body');
      var text = el('div', 'library-card__text');
      var h3 = el('h3', 'library-card__heading');
      if (cardUrl) {
        var titleLink = el('a', 'library-card__title link');
        titleLink.href = cardUrl;
        titleLink.textContent = r.title || '';
        h3.appendChild(titleLink);
      } else {
        var titleSpan = el('span', 'library-card__title');
        titleSpan.textContent = r.title || '';
        h3.appendChild(titleSpan);
      }
      text.appendChild(h3);
      if (r.description) {
        var summary = el('p', 'library-card__summary');
        summary.textContent = r.description;
        text.appendChild(summary);
      }

      // Format label + file size. The size arrives asynchronously (HEAD), so
      // it starts hidden and reveals itself only if the host reports a length.
      if (fileFmt || cardUrl) {
        var meta = el('p', 'library-card__meta');
        if (fileFmt) {
          var badge = el('span', 'library-card__badge');
          badge.textContent = fileFmt;
          meta.appendChild(badge);
        }
        if (cardUrl && showSize && isTrustedSource(cardUrl, sizeSources)) {
          var size = el('span', 'library-card__size');
          size.hidden = true;
          size.setAttribute('data-file-url', cardUrl);
          size.__strings = strings;
          meta.appendChild(size);
          var known = sizeCache[cardUrl];
          if (known > 0) {
            size.textContent = formatSize(known, strings);
            size.hidden = false;
          } else if (known === undefined) {
            observeSizes(li);
          }
        }
        text.appendChild(meta);
      }
      body.appendChild(text);

      var actions = el('div', 'library-card__actions');
      if (cardUrl) {
        var dl = el('a', 'library-card__action library-card__action--download');
        dl.href = cardUrl;
        if (isExternal) { dl.target = '_blank'; dl.rel = 'noopener'; } else { dl.setAttribute('download', ''); }
        dl.setAttribute('aria-label', format(strings.download_aria, { TITLE: r.title, FORMAT: r.file_format }));
        dl.innerHTML = ICON.download;
        actions.appendChild(dl);
      }
      var email = el('button', 'library-card__action');
      email.type = 'button';
      email.setAttribute('data-library-email', '');
      email.setAttribute('data-file', cardUrl);
      email.setAttribute('data-title', r.title || '');
      email.setAttribute('aria-label', format(strings.email, { TITLE: r.title }));
      email.innerHTML = ICON.email;
      actions.appendChild(email);

      var isSaved = savedSet.has(r.handle);
      var save = el('button', 'library-card__action library-card__action--save' + (isSaved ? ' is-saved' : ''));
      save.type = 'button';
      save.setAttribute('data-library-save', '');
      save.setAttribute('data-resource', r.handle || '');
      save.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
      save.setAttribute('aria-label', format(isSaved ? strings.unsave : strings.save, { TITLE: r.title }));
      save.innerHTML = ICON.bookmark;
      actions.appendChild(save);
      if (isSaved) card.classList.add('library-card--saved');
      body.appendChild(actions);

      card.appendChild(body);
      return li;
    }

    function renderLoading() {
      resultsEl.setAttribute('aria-busy', 'true');
      resultsEl.hidden = false;
      releaseSizeNodes(resultsEl);
      if (stateEl) { stateEl.hidden = true; stateEl.textContent = ''; }
      var frag = document.createDocumentFragment();
      var n = Math.min(perPage, resultsEl.children.length || 6);
      for (var i = 0; i < n; i += 1) frag.appendChild(buildSkeleton());
      resultsEl.replaceChildren(frag);
    }

    function buildSkeleton() {
      var li = el('li', 'grid__item library-item library-card--skeleton');
      li.setAttribute('aria-hidden', 'true');
      var wrapper = el('div', 'library-card-wrapper card-wrapper');
      var card = el('div', 'card library-card ' + ratioClass + ' contains-card--product card--card card--media color-' + cardScheme + ' gradient');
      var band = el('div', 'card__inner ratio library-card__band');
      var media = el('div', 'card__media');
      media.appendChild(el('div', 'media media--transparent library-skeleton__block block-when-empty'));
      band.appendChild(media);
      card.appendChild(band);
      var body = el('div', 'library-card__body');
      ['type', 'title', 'title-short', 'summary'].forEach(function (k) {
        body.appendChild(el('div', 'library-skeleton__block block-when-empty library-skeleton__line library-skeleton__line--' + k));
      });
      card.appendChild(body);
      wrapper.appendChild(card);
      li.appendChild(wrapper);
      return li;
    }

    function renderCount(n) {
      if (!countEl) return;
      var tmpl = n === 1 ? strings.count_one : strings.count_other;
      countEl.textContent = format(tmpl, { COUNT: numberFmt(n) });
    }

    function numberFmt(n) {
      try { return new Intl.NumberFormat(document.documentElement.lang || 'en').format(n); }
      catch (e) { return String(n); }
    }

    function renderFacets(facets) {
      var groupsSeen = [];
      facetInputs.forEach(function (input) {
        var key = input.getAttribute('data-library-facet');
        var checked = state[key].indexOf(input.value) > -1;
        var group = facets[key] || [];
        var match = null;
        for (var i = 0; i < group.length; i += 1) { if (group[i].value === input.value) { match = group[i]; break; } }
        var count = match ? match.count : 0;
        var label = root.querySelector('label[for="' + input.id + '"]');
        var results = label ? label.querySelector('.facet__results') : null;
        if (results) results.textContent = '(' + numberFmt(count) + ')';
        input.checked = checked;
        if (label) label.classList.toggle('active', checked);
        // Hide options with nothing behind them (keep the node so they return
        // when the counts change); a selected filter always stays visible.
        var item = input.closest('.facets__item');
        if (item) item.hidden = count === 0 && !checked;
        var groupEl = input.closest('.library__facet-group');
        if (groupEl && groupsSeen.indexOf(groupEl) === -1) groupsSeen.push(groupEl);
      });
      // Hide a whole group when it has no visible options left.
      groupsSeen.forEach(function (groupEl) {
        var hasVisible = slice(groupEl.querySelectorAll('.facets__item')).some(function (li) { return !li.hidden; });
        groupEl.hidden = !hasVisible;
      });
    }

    function syncFacetChecks() {
      facetInputs.forEach(function (input) {
        input.checked = state[input.getAttribute('data-library-facet')].indexOf(input.value) > -1;
      });
    }

    function activeFilters() {
      var list = [];
      GROUPS.forEach(function (g) {
        state[g].forEach(function (v) {
          var meta = valueMeta[g + ':' + v] || { label: v, groupLabel: g };
          list.push({ group: g, value: v, label: meta.label, groupLabel: meta.groupLabel });
        });
      });
      return list;
    }

    function renderChips() {
      if (!chipsEl) return;
      var active = activeFilters();
      chipsEl.hidden = active.length === 0;
      chipsEl.textContent = '';
      if (!active.length) return;
      active.forEach(function (f) {
        var btn = el('button', 'library-chips__button active-facets__button');
        btn.type = 'button';
        btn.setAttribute('data-remove-group', f.group);
        btn.setAttribute('data-remove-value', f.value);
        var inner = el('span', 'active-facets__button-inner button button--tertiary');
        var lbl = el('span');
        lbl.textContent = f.groupLabel + ': ' + f.label;
        inner.appendChild(lbl);
        inner.insertAdjacentHTML('beforeend', ICON.closeSmall);
        btn.appendChild(inner);
        btn.setAttribute('aria-label', format(strings.remove_filter, { FILTER: f.groupLabel + ': ' + f.label }));
        chipsEl.appendChild(btn);
      });
      var clear = el('button', 'library-chips__clear un-underlined-link link');
      clear.type = 'button';
      clear.setAttribute('data-clear-all', '');
      clear.textContent = strings.clear_all;
      chipsEl.appendChild(clear);
    }

    function renderEmpty(count, query) {
      if (!stateEl) return;
      if (count > 0) { stateEl.hidden = true; stateEl.textContent = ''; return; }
      stateEl.hidden = false;
      stateEl.textContent = '';
      var heading = el('p', 'h4-size mb-1');
      heading.textContent = state.q ? format(strings.empty_with_query, { QUERY: query || state.q }) : strings.empty;
      stateEl.appendChild(heading);
      var active = activeFilters();
      if (active.length) {
        var listed = el('p', 'library-state__detail');
        listed.textContent = format(strings.empty_filters, { FILTERS: active.map(function (f) { return f.groupLabel + ': ' + f.label; }).join(', ') });
        stateEl.appendChild(listed);
      }
      var hint = el('p', 'library-state__detail');
      hint.textContent = strings.empty_hint;
      stateEl.appendChild(hint);
      if (active.length) {
        var clear = el('button', 'button button--secondary mt-1');
        clear.type = 'button';
        clear.setAttribute('data-clear-all', '');
        clear.textContent = strings.clear_all;
        stateEl.appendChild(clear);
      }
    }

    function renderError() {
      resultsEl.setAttribute('aria-busy', 'false');
      resultsEl.replaceChildren();
      resultsEl.hidden = true;
      if (pagerEl) pagerEl.textContent = '';
      if (!stateEl) return;
      stateEl.hidden = false;
      stateEl.textContent = '';
      var heading = el('p', 'h4-size mb-1');
      heading.textContent = strings.error;
      stateEl.appendChild(heading);
      var retry = el('button', 'button button--secondary mt-1');
      retry.type = 'button';
      retry.setAttribute('data-library-retry', '');
      retry.textContent = strings.retry;
      stateEl.appendChild(retry);
      retry.focus();
    }

    function renderPager(totalPages, page) {
      if (!pagerEl) return;
      pagerEl.textContent = '';
      if (totalPages <= 1) return;
      var ul = el('ul', 'pagination__list overflow-hidden inline-flex flex-wrap justify-center list-unstyled');
      ul.setAttribute('role', 'list');
      if (page > 1) ul.appendChild(pagerArrow(page - 1, 'prev', strings.pagination_previous));
      pageWindow(page, totalPages).forEach(function (p) { ul.appendChild(p === 'gap' ? pagerGap() : pagerItem(p, page)); });
      if (page < totalPages) ul.appendChild(pagerArrow(page + 1, 'next', strings.pagination_next));
      pagerEl.appendChild(ul);
    }

    function pagerItem(p, current) {
      var li = el('li', 'flex');
      var a = el('a', 'pagination__item full-unstyled-link' + (p === current ? ' pagination__item--current font-bold' : ''));
      a.href = pageUrl(p);
      a.textContent = String(p);
      a.setAttribute('data-page', String(p));
      a.setAttribute('data-no-instant', '');
      a.setAttribute('aria-label', format(strings.pagination_page, { NUMBER: p }));
      if (p === current) a.setAttribute('aria-current', 'page');
      li.appendChild(a);
      return li;
    }

    function pagerArrow(p, dir, label) {
      var li = el('li', 'flex');
      var a = el('a', 'pagination__item pagination__item-arrow pagination__item--' + dir + ' full-unstyled-link motion-reduce');
      a.href = pageUrl(p);
      a.setAttribute('data-page', String(p));
      a.setAttribute('data-no-instant', '');
      a.setAttribute('aria-label', label);
      a.innerHTML = ICON.caret;
      li.appendChild(a);
      return li;
    }

    function pagerGap() {
      var li = el('li', 'flex');
      var span = el('span', 'pagination__item');
      span.textContent = '…';
      li.appendChild(span);
      return li;
    }

    function pageWindow(page, total) {
      var set = {};
      [1, total, page, page - 1, page + 1].forEach(function (p) { if (p >= 1 && p <= total) set[p] = true; });
      var sorted = Object.keys(set).map(Number).sort(function (a, b) { return a - b; });
      var out = [];
      sorted.forEach(function (p, i) { if (i > 0 && p - sorted[i - 1] > 1) out.push('gap'); out.push(p); });
      return out;
    }

    function pageUrl(p) {
      var prev = state.page; state.page = p;
      var url = buildUrl(); state.page = prev;
      return url;
    }

    function scrollToTop() {
      var top = resultsEl.getBoundingClientRect().top;
      if (top >= 0) return;
      var header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: top + window.scrollY - header, behavior: reduce ? 'auto' : 'smooth' });
    }

    // ---- control syncing ----------------------------------------------
    function syncControls() {
      if (searchInput && searchInput.value !== state.q) searchInput.value = state.q;
      sortSelects.forEach(function (s) { if (s.value !== state.sort) s.value = state.sort; });
      var toggle = root.querySelector('[data-library-saved-toggle]');
      if (toggle) toggle.checked = state.saved;
      syncFacetChecks();
    }

    // ---- events -------------------------------------------------------
    var onSearch = debounce(function () {
      var v = searchInput ? searchInput.value.trim() : '';
      if (v === state.q) return;
      state.q = v; state.page = 1;
      pushUrl(); load();
    }, SEARCH_DEBOUNCE);

    if (searchForm) searchForm.addEventListener('submit', function (e) { e.preventDefault(); onSearch(); });
    if (searchInput) searchInput.addEventListener('input', onSearch);

    facetInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        var key = input.getAttribute('data-library-facet');
        var arr = state[key];
        var idx = arr.indexOf(input.value);
        if (input.checked && idx === -1) arr.push(input.value);
        if (!input.checked && idx > -1) arr.splice(idx, 1);
        state.page = 1;
        pushUrl(); load();
      });
    });

    sortSelects.forEach(function (sel) {
      sel.addEventListener('change', function () {
        state.sort = sel.value; state.page = 1;
        syncControls(); pushUrl(); load();
      });
    });

    var savedToggle = root.querySelector('[data-library-saved-toggle]');
    if (savedToggle) {
      savedToggle.addEventListener('change', function () {
        // Guests can't have saved resources — send them to log in.
        if (!loggedIn || savedToggle.hasAttribute('data-requires-login')) {
          savedToggle.checked = false;
          navigate(loginUrlNow());
          return;
        }
        state.saved = savedToggle.checked; state.page = 1;
        pushUrl(); load({ scroll: true });
      });
    }

    // ---- email + save actions ----------------------------------------
    function openEmail(btn) {
      var url = btn.getAttribute('data-file') || '';
      if (!url) return;
      var title = btn.getAttribute('data-title') || '';
      var subject = format(strings.email_subject, { TITLE: title });
      var bodyText = format(strings.email_body, { TITLE: title, URL: url });
      navigate('mailto:?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(bodyText));
    }

    function saveButtonsFor(handle) {
      return slice(root.querySelectorAll('[data-library-save]')).filter(function (b) {
        return b.getAttribute('data-resource') === handle;
      });
    }

    function applySavedState(handle, saved) {
      if (saved) savedSet.add(handle); else savedSet.delete(handle);
      saveButtonsFor(handle).forEach(function (btn) {
        btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
        btn.classList.toggle('is-saved', saved);
        var card = btn.closest('.library-card');
        if (card) card.classList.toggle('library-card--saved', saved);
        var titleEl = card ? card.querySelector('.library-card__title') : null;
        var title = titleEl ? titleEl.textContent.trim() : '';
        btn.setAttribute('aria-label', format(saved ? strings.unsave : strings.save, { TITLE: title }));
      });
    }

    function setSaveBusy(handle, busy) {
      saveButtonsFor(handle).forEach(function (btn) {
        btn.disabled = busy;
        btn.setAttribute('aria-busy', busy ? 'true' : 'false');
      });
    }

    function toggleSave(btn) {
      var handle = btn.getAttribute('data-resource');
      if (!handle) return;
      if (!loggedIn) {
        try { window.sessionStorage.setItem(PENDING_SAVE_KEY, handle); } catch (e) { /* private mode */ }
        navigate(loginUrlNow());
        return;
      }
      var was = savedSet.has(handle);
      var next = !was;
      applySavedState(handle, next); // optimistic
      setSaveBusy(handle, true);
      window.LibraryDataSource.save(handle, next ? 'save' : 'unsave').then(function (resp) {
        setSaveBusy(handle, false);
        applySavedState(handle, resp.saved);
        // In the saved-only view, unsaving should drop the card out.
        if (state.saved && !resp.saved) load({ force: true });
      }).catch(function (err) {
        setSaveBusy(handle, false);
        applySavedState(handle, was); // revert
        if (err && err.code === 'unauthenticated') { navigate(loginUrlNow()); return; }
        announce(strings.save_error);
      });
    }

    // Auto-complete a save the guest attempted before logging in.
    function resumePendingSave() {
      if (!loggedIn) return;
      var pending;
      try { pending = window.sessionStorage.getItem(PENDING_SAVE_KEY); } catch (e) { pending = null; }
      if (!pending) return;
      try { window.sessionStorage.removeItem(PENDING_SAVE_KEY); } catch (e) { /* ignore */ }
      if (savedSet.has(pending)) return;
      window.LibraryDataSource.save(pending, 'save').then(function (resp) {
        applySavedState(pending, resp.saved);
      }).catch(function () { /* silent */ });
    }

    root.addEventListener('click', function (e) {
      var emailBtn = e.target.closest('[data-library-email]');
      if (emailBtn) { e.preventDefault(); openEmail(emailBtn); return; }
      var saveBtn = e.target.closest('[data-library-save]');
      if (saveBtn) { e.preventDefault(); toggleSave(saveBtn); return; }
      var remove = e.target.closest('[data-remove-group]');
      if (remove) {
        e.preventDefault();
        var g = remove.getAttribute('data-remove-group');
        var v = remove.getAttribute('data-remove-value');
        var i = state[g].indexOf(v);
        if (i > -1) state[g].splice(i, 1);
        state.page = 1; syncControls(); pushUrl(); load();
        return;
      }
      if (e.target.closest('[data-clear-all]')) {
        e.preventDefault();
        GROUPS.forEach(function (g) { state[g] = []; });
        state.page = 1; syncControls(); pushUrl(); load();
        return;
      }
      if (e.target.closest('[data-library-retry]')) {
        e.preventDefault();
        load({ force: true });
        return;
      }
      var pageLink = e.target.closest('[data-page]');
      if (pageLink) {
        e.preventDefault();
        state.page = parseInt(pageLink.getAttribute('data-page'), 10) || 1;
        pushUrl(); load({ scroll: true, force: true });
      }
    });

    window.addEventListener('popstate', function () {
      state = parseState();
      syncControls();
      load();
    });

    // ---- boot ---------------------------------------------------------
    syncControls();
    resumePendingSave();              // finish a save that triggered a login
    load();                           // proxy drives the first paint too
  }

  function readStrings(root) {
    var node = root.querySelector('[data-library-strings]');
    if (!node) return {};
    try { return JSON.parse(node.textContent); } catch (e) { return {}; }
  }

  function addWidth(url, w) {
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function sameHost(url) {
    try { return new URL(url, window.location.href).host === window.location.host; } catch (e) { return false; }
  }

  function init() {
    slice(document.querySelectorAll('.library__root')).forEach(function (root) {
      initViewToggle(root);
      initFilterDrawer(root);
      initLibrary(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
